// localStorage adapter for local stats.
//
// `stats.ts` deliberately holds all the rules as pure functions over a plain record, so this file
// only has to answer one question: how do we read and write that record without ever throwing.
//
// It must never throw, and that is not defensive habit — it is the whole reason this is a separate
// file. This runs immediately after a player finishes a run. A quota error, a Safari private-mode
// `localStorage` that throws on access, or a record corrupted by a half-written earlier version
// would otherwise take out the results screen at the exact moment the player has something to show
// for the last thirty seconds. Losing the streak is a bad outcome; losing the score you just earned
// is a much worse one, so every failure path here degrades to "no stats" and lets the run stand.
//
// The store is injected rather than reached for globally, which keeps this DOM-free and testable in
// node like the rest of the engine.

import { EMPTY_STATS, type Stats, type StatsEntry } from '../engine/stats'

/** The part of the Web Storage API this needs. Anything with these two methods will do. */
export interface StorageLike {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
}

/**
 * Versioned, because the shape is going to change — the hub becomes the authority on streaks at M6
 * and an archive view lands at M9. A new key is a clean reset; a silent shape mismatch is not.
 */
export const STATS_KEY = 'bigdog.stats.v1'

/**
 * `localStorage`, or null where it cannot be used.
 *
 * Accessing the property itself throws in Safari private browsing and in a sandboxed iframe, so the
 * access is inside the try rather than merely the call.
 */
export function browserStorage(): StorageLike | null {
  try {
    return globalThis.localStorage ?? null
  } catch {
    return null
  }
}

function isEntry(value: unknown): value is StatsEntry {
  if (typeof value !== 'object' || value === null) return false
  const e = value as Record<string, unknown>
  return (
    typeof e.key === 'string' &&
    typeof e.score === 'number' &&
    typeof e.durationMs === 'number' &&
    typeof e.won === 'boolean'
  )
}

/**
 * Validate rather than trust.
 *
 * The stored JSON is user-writable — a devtools console is all it takes — and it is read straight
 * into the code that computes the streak. A string where a number belongs would propagate NaN
 * through every counter rather than failing anywhere near the cause.
 */
function isStats(value: unknown): value is Stats {
  if (typeof value !== 'object' || value === null) return false
  const s = value as Record<string, unknown>
  return (
    Array.isArray(s.history) &&
    s.history.every(isEntry) &&
    typeof s.played === 'number' &&
    typeof s.won === 'number' &&
    typeof s.currentStreak === 'number' &&
    typeof s.bestStreak === 'number' &&
    typeof s.bestScore === 'number'
  )
}

/** Never throws. Anything unreadable, unparseable, or the wrong shape reads as a fresh player. */
export function loadStats(store: StorageLike | null): Stats {
  if (!store) return EMPTY_STATS
  try {
    const raw = store.getItem(STATS_KEY)
    if (!raw) return EMPTY_STATS
    const parsed: unknown = JSON.parse(raw)
    return isStats(parsed) ? parsed : EMPTY_STATS
  } catch {
    return EMPTY_STATS
  }
}

/** Never throws. A full disk costs the player their streak, not their score. */
export function saveStats(store: StorageLike | null, stats: Stats): boolean {
  if (!store) return false
  try {
    store.setItem(STATS_KEY, JSON.stringify(stats))
    return true
  } catch {
    return false
  }
}
